/** Выбранная позиция из импортированного MIS-плана для ИИ-разнесения (LK parity). */
export type SelectedPlanService = {
  source_plan_id: string
  item_id: string
  snapshot_id: string
  display_name: string
  tooth: string | null
  quantity: number
  expected_cost_minor: number
}

export function selectedServiceKey(service: SelectedPlanService): string {
  return `${service.source_plan_id}:${service.item_id}`
}

export type MisImportedPlanSummary = {
  plan_id: string
  plan_title?: string | null
  plan_date?: string | null
  created_at?: string | null
  doctor_display?: string | null
  doctor_name?: string | null
  item_count?: number
  import_source?: string | null
  metadata?: Record<string, unknown> | null
  total_estimated_minor?: number | null
  notes?: string | null
}

export function misImportedPlanComposeAt(plan: MisImportedPlanSummary): Date | null {
  const raw = plan.created_at ?? plan.plan_date
  if (!raw) return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}

export function misImportedPlanComposeTimeLabel(plan: MisImportedPlanSummary): string | null {
  const date = misImportedPlanComposeAt(plan)
  if (!date) return null
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}


export function misImportedPlanDateLabel(plan: MisImportedPlanSummary): string {
  const date = misImportedPlanComposeAt(plan)
  return date ? date.toLocaleDateString('ru-RU') : '—'
}

export function misImportedPlanDoctorLabel(plan: MisImportedPlanSummary): string {
  const name = (plan.doctor_display ?? plan.doctor_name ?? '').trim()
  return name || 'Врач не указан'
}

export function misImportedPlanTitleLabel(plan: MisImportedPlanSummary): string {
  const title = (plan.plan_title ?? '').trim()
  const count = plan.item_count ?? '?'
  return title ? `${title} (${count})` : `План (${count})`
}

export function misImportedServicesFromPlan(plan: {
  plan_id: string
  stages?: Array<{
    items?: Array<{
      item_id?: string
      snapshot_id?: string
      display_name?: string
      quantity?: number
      expected_cost_minor?: number
      dental_scope?: { teeth?: unknown[] } | null
    }>
  }>
}): SelectedPlanService[] {
  const out: SelectedPlanService[] = []
  for (const stage of plan.stages ?? []) {
    for (const item of stage.items ?? []) {
      const itemId = String(item.item_id ?? '')
      const snapshotId = String(item.snapshot_id ?? '')
      if (!itemId || !snapshotId) continue
      const teeth = Array.isArray(item.dental_scope?.teeth)
        ? item.dental_scope!.teeth!.map(String)
        : []
      out.push({
        source_plan_id: plan.plan_id,
        item_id: itemId,
        snapshot_id: snapshotId,
        display_name: item.display_name ?? '\u0423\u0441\u043b\u0443\u0433\u0430',
        tooth: teeth[0] ?? null,
        quantity: item.quantity ?? 1,
        expected_cost_minor: item.expected_cost_minor ?? 0,
      })
    }
  }
  return out
}


export function isAiGeneratedMisPlan(plan: MisImportedPlanSummary & { metadata?: Record<string, unknown> | null }): boolean {
  const meta = plan.metadata ?? {}
  return Boolean(
    meta.diagnostics_compose || meta.diagnostics_compose_fallback || meta.ai_voice_compose || meta.ai_assist
  )
}

export function misImportedServicesToMisServiceItems(
  planId: string,
  services: SelectedPlanService[],
): import('../constructorTypes').MisServiceItem[] {
  return services.map((s, index) => ({
    id: `${planId}:${s.item_id}:${index}`,
    service_name: s.display_name,
    tooth_number: s.tooth ?? '',
    quantity: s.quantity,
    unit_price: s.expected_cost_minor > 0 ? s.expected_cost_minor / 100 : undefined,
    total_price: s.expected_cost_minor > 0 ? (s.expected_cost_minor / 100) * (s.quantity ?? 1) : undefined,
    source_doctor_id: null,
    source_doctor_name: null,
    mis_plan_uuid: planId,
  }))
}


export function misImportedPlanTitleForSidebar(plan: MisImportedPlanSummary): string {
  const title = (plan.plan_title ?? "").trim()
  if (title) return title
  const notes = (plan as MisImportedPlanSummary & { notes?: string | null }).notes?.trim()
  if (notes) return notes.length > 60 ? notes.slice(0, 57) + "…" : notes
  return "План лечения (ИИ)"
}
